import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { getRatingCategories, createRatingCategory, updateRatingCategory, deleteRatingCategory } from '@/lib/api';
import { Plus, Pencil, Trash2, Star, GripVertical } from 'lucide-react';

export const RatingCategoriesTab = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name_de: '', name_en: '', description: '', sort_order: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRatingCategories();
      setCategories(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditItem(null);
    setForm({ name_de: '', name_en: '', description: '', sort_order: categories.length + 1 });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      name_de: item.name_de || '',
      name_en: item.name_en || '',
      description: item.description || '',
      sort_order: item.sort_order || 0,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name_de.trim()) {
      toast.error(t('admin.rating.nameDeRequired'));
      return;
    }
    try {
      if (editItem) {
        await updateRatingCategory(editItem.category_id, form);
        toast.success(t('admin.rating.categoryUpdated'));
      } else {
        await createRatingCategory(form);
        toast.success(t('admin.rating.categoryCreated'));
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.rating.error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.rating.confirmDelete'))) return;
    try {
      await deleteRatingCategory(id);
      toast.success(t('admin.rating.deleted'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.rating.error'));
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await updateRatingCategory(item.category_id, { active: !item.active });
      toast.success(item.active ? t('admin.rating.deactivated') : t('admin.rating.activated'));
      load();
    } catch { toast.error(t('admin.rating.error')); }
  };

  return (
    <Card data-testid="rating-categories-tab">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Star className="h-5 w-5 text-amber-500" />
          {t('admin.rating.title')}
        </CardTitle>
        <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={openAdd} data-testid="add-rating-category-btn">
          <Plus className="h-4 w-4 mr-1" /> {t('admin.rating.add')}
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          {t('admin.rating.defineCriteria')}
        </p>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
          </div>
        ) : categories.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">{t('admin.rating.noCategories')}</p>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="rating-categories-table">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th className="text-left px-4 py-3 font-medium w-10">#</th>
                  <th className="text-left px-4 py-3 font-medium">{t('admin.units.nameDe')}</th>
                  <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">{t('admin.units.nameEn')}</th>
                  <th className="text-center px-4 py-3 font-medium">{t('admin.rating.active')}</th>
                  <th className="text-right px-4 py-3 font-medium">{t('admin.rating.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {categories.map((cat) => (
                  <tr key={cat.category_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`rating-cat-row-${cat.category_id}`}>
                    <td className="px-4 py-3 text-muted-foreground">
                      <GripVertical className="h-4 w-4 inline" /> {cat.sort_order}
                    </td>
                    <td className="px-4 py-3 font-medium">{cat.name_de}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{cat.name_en}</td>
                    <td className="px-4 py-3 text-center">
                      <Switch
                        checked={cat.active}
                        onCheckedChange={() => handleToggleActive(cat)}
                        data-testid={`toggle-active-${cat.category_id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(cat)} data-testid={`edit-rating-cat-${cat.category_id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.category_id)} data-testid={`delete-rating-cat-${cat.category_id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="rating-category-dialog">
          <DialogHeader>
            <DialogTitle>{editItem ? t('admin.rating.editCategory') : t('admin.rating.addCategory')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('admin.units.nameDe')} *</Label>
              <Input value={form.name_de} onChange={(e) => setForm(f => ({ ...f, name_de: e.target.value }))} placeholder={t('admin.rating.exampleReliability')} data-testid="rating-cat-name-de" />
            </div>
            <div>
              <Label>{t('admin.units.nameEn')}</Label>
              <Input value={form.name_en} onChange={(e) => setForm(f => ({ ...f, name_en: e.target.value }))} data-testid="rating-cat-name-en" />
            </div>
            <div>
              <Label>{t('admin.rating.description')}</Label>
              <Input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t('admin.rating.descriptionPlaceholder')} />
            </div>
            <div>
              <Label>{t('admin.rating.sortOrder')}</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={handleSave} data-testid="save-rating-cat-btn">
              {editItem ? t('common.save') : t('admin.rating.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
